import { prescriptionService } from '@/services/prescriptionService';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiResponse } from '@/services/api';
import { PrescriptionLinkResponse } from '@/services/prescriptionService';

export interface PrescriptionMedicationRequest {
  id: string;
  medication: string;
  medication_name?: string;
  dose: string;
  sig: string;
  quantity_authorized: number;
  unit_of_measure: string;
  day_supply?: number | null;
  days_supply?: string | null;
  refills_authorized?: number;
  price?: number;
  selling_price_cents?: number;
  selling_price_dollars?: number;
  base_price_cents?: number;
  base_price_dollars?: number;
  discount_cents?: number;
  product?: {
    id: string;
    /** Pharmetika template / product id for BUD and shipping dimensions */
    pharmetika_id?: string;
    description: string;
    ndc: string;
    rxcui: string;
    controlled_indicator: string;
    compound_indicator: string;
    dea_schedule_level: string;
    base_price_cents: number;
    base_price_dollars: number;
    clinic_price_cents: number;
    clinic_price_dollars: number;
    effective_price_cents: number;
    effective_price_dollars: number;
    created_at: string;
    updated_at: string;
    price_plan_cost?: number;
  };
  clinicProduct?: {
    id: string;
    selling_price: number;
    base_price: number;
    getSellingPriceInDollars?: () => number;
    getBasePriceInDollars?: () => number;
  };
}

export interface PrescriptionCartItem extends PrescriptionMedicationRequest {
  cartPrice: number; // Final price to use in cart calculations
  medication_name: string;
}

export interface PrescriptionCartMetadata {
  prescriptionId: string;
  prescriptionLinkId: string;
  patientId: string;
  token: string;
}

export interface PrescriptionCartState {
  items: PrescriptionCartItem[];
  metadata: PrescriptionCartMetadata | null;
  isLoading: boolean;
}

export interface UsePrescriptionCartReturn {
  items: PrescriptionCartItem[];
  metadata: PrescriptionCartMetadata | null;
  isLoading: boolean;
  cartCount: number;
  cartTotal: number;
  setPrescriptionItems: (
    medicationRequests: PrescriptionMedicationRequest[],
    metadata: PrescriptionCartMetadata
  ) => void;
  clearPrescriptionCart: () => void;
  getPrescriptionTotal: () => number;
  updateItemQuantity: (itemId: string, quantity: number) => void;
  removeItem: (itemId: string) => void;
  setState: (state: PrescriptionCartState) => void;
}

export const usePrescriptionCart = (): UsePrescriptionCartReturn => {
  const { token } = useParams();

  const [state, setState] = useState<PrescriptionCartState>({
    items: [],
    metadata: null,
    isLoading: false,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['prescription-cart', token],
    queryFn: () => prescriptionService.getPrescriptionLink(token!),
    enabled: !!token,
  });

  // Handle successful data loading
  useEffect(() => {
    if (data?.success && data.data) {
      const { prescriptionLink, prescription, patient } = data.data;
      const items = prescription.medicationRequests.map((request: PrescriptionMedicationRequest) => ({
        ...request,
        cartPrice: request.clinicProduct?.selling_price ? request.clinicProduct.selling_price / 100 : 0,
        medication_name: request.product?.description || request.medication_name || request.medication,
      }));
      setState(state => ({ ...state, items }));
    }
  }, [data]);



  const setPrescriptionItems = (
    medicationRequests: PrescriptionMedicationRequest[],
    metadata: PrescriptionCartMetadata
  ) => {
    const cartItems: PrescriptionCartItem[] = medicationRequests.map(request => {
      let cartPrice = request.clinicProduct?.selling_price ? request.clinicProduct.selling_price / 100 : request.clinicProduct?.base_price ? request.clinicProduct.base_price / 100 : (request.product?.price_per_unit || request.product?.price_plan_cost || 0);

      return {
        ...request,
        cartPrice,
        medication_name: request.product?.description || request.medication_name || request.medication,
      };
    });


    setState({
      items: cartItems,
      metadata,
      isLoading: false,
    });
  }

  const clearPrescriptionCart = useCallback(() => {
    setState({
      items: [],
      metadata: null,
      isLoading: false,
    });
  }, []);

  const updateItemQuantity = useCallback((itemId: string, quantity: number) => {
    setState(prevState => ({
      ...prevState,
      items: prevState.items.map(item =>
        item.id === itemId
          ? { ...item, quantity_authorized: Math.max(0, quantity) }
          : item
      ),
    }));
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setState(prevState => ({
      ...prevState,
      items: prevState.items.filter(item => item.id !== itemId),
    }));
  }, []);

  // Derived values using useMemo for performance
  const cartCount = state.items.length;

  const subtotalBeforeDiscount = state.items.reduce(
    (total, item) => total + item.cartPrice * (Number(item.quantity_authorized) || 1),
    0
  );

  const totalDiscountDollars = state.items.reduce(
    (total, item) => total + (Number(item.discount_cents) || 0) / 100,
    0
  );

  const cartTotal = subtotalBeforeDiscount - totalDiscountDollars;

  const getPrescriptionTotal = useCallback(() => cartTotal, [cartTotal]);

  return {
    items: state.items,
    metadata: state.metadata,
    isLoading: state.isLoading,
    cartCount,
    cartTotal,
    subtotalBeforeDiscount,
    totalDiscountDollars,
    setPrescriptionItems,
    clearPrescriptionCart,
    getPrescriptionTotal,
    updateItemQuantity,
    removeItem,
    setState,
  };
}; 